import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect, useCallback } from 'react';
import { ProtectedRoute } from '../../components/ProtectedRoute';
import { useAuth } from '../../hooks/useAuth';
import { DEFAULT_COMPANY_ID } from '../../lib/config';
import {
  listPositions,
  createPosition,
  updatePosition,
  deactivatePosition,
  activatePosition,
  type PositionDto,
  type PositionCompetencyRequirement,
} from '../../lib/positions';
import {
  listCompetencies,
  type CompetencyDto,
} from '../../lib/competencies';
import {
  Briefcase,
  Plus,
  Trash2,
  Check,
  Target,
  Info,
  Star,
} from 'lucide-react';
import { Card, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Slider } from '@/components/ui/slider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

export const Route = createFileRoute('/kinerja/positions')({
  component: PositionsPage,
});

function PositionsPage() {
  return (
    <ProtectedRoute minimumRole="hr">
      <PositionsContent />
    </ProtectedRoute>
  );
}



function PositionsContent() {
  const { user } = useAuth();
  const companyId = user?.companyId ?? DEFAULT_COMPANY_ID;

  // ── Positions ───────────────────────────────────────────────────────────────
  const [positions, setPositions] = useState<PositionDto[]>([]);
  const [posLoading, setPosLoading] = useState(true);
  const [selectedPosId, setSelectedPosId] = useState<string | null>(null);
  const [showInactive, setShowInactive] = useState(false);

  // create position
  const [addingPos, setAddingPos] = useState(false);
  const [newPos, setNewPos] = useState({ code: '', title: '', description: '' });
  const [posSaving, setPosSaving] = useState(false);
  const [posError, setPosError] = useState('');

  // ── Competency requirements ─────────────────────────────────────────────────
  const [requirements, setRequirements] = useState<PositionCompetencyRequirement[]>([]);
  const [reqSaving, setReqSaving] = useState(false);
  const [reqDirty, setReqDirty] = useState(false);

  // add requirement row
  const [addingReq, setAddingReq] = useState(false);
  const [newReq, setNewReq] = useState({
    competencyId: '',
    competencyCode: '',
    requiredLevel: 3,
    weight: 1,
    isCritical: false,
  });

  // all competencies (for dropdown)
  const [allCompetencies, setAllCompetencies] = useState<CompetencyDto[]>([]);
  const [compsLoaded, setCompsLoaded] = useState(false);

  // ── Load positions ──────────────────────────────────────────────────────────
  const loadPositions = useCallback(
    (includeInactive: boolean) => {
      let cancelled = false;
      setPosLoading(true);
      listPositions(companyId, includeInactive).then((res) => {
        if (cancelled) return;
        setPosLoading(false);
        if (res.success && res.data) {
          setPositions(res.data.positions);
          if (res.data.positions.length > 0 && !selectedPosId) {
            setSelectedPosId(res.data.positions[0].id);
          }
        }
      });
      return () => { cancelled = true; };
    },
    [companyId], // eslint-disable-line react-hooks/exhaustive-deps
  );

  useEffect(() => {
    const cleanup = loadPositions(showInactive);
    return cleanup;
  }, [loadPositions, showInactive]);

  // ── Load all competencies once ──────────────────────────────────────────────
  useEffect(() => {
    if (compsLoaded) return;
    let cancelled = false;
    listCompetencies({ companyId }).then((res) => {
      if (cancelled) return;
      setCompsLoaded(true);
      if (res.success && res.data) setAllCompetencies(res.data.competencies);
    });
    return () => { cancelled = true; };
  }, [companyId, compsLoaded]);

  // ── Sync requirements when selected position changes ───────────────────────
  useEffect(() => {
    const pos = positions.find((p) => p.id === selectedPosId);
    if (pos) {
      setRequirements(pos.competencyRequirements ?? []);
      setReqDirty(false);
      setAddingReq(false);
    }
  }, [selectedPosId, positions]);

  // ── Position actions ────────────────────────────────────────────────────────
  async function handleCreatePosition(e: React.FormEvent) {
    e.preventDefault();
    if (!newPos.code.trim() || !newPos.title.trim()) return;
    setPosSaving(true);
    setPosError('');
    const res = await createPosition(
      {
        companyId,
        code: newPos.code.trim().toUpperCase(),
        title: newPos.title.trim(),
        description: newPos.description.trim() || undefined,
        competencyRequirements: [],
      },
    );
    setPosSaving(false);
    if (res.success && res.data) {
      toast.success('Position created');
      setPositions((prev) => [...prev, res.data!.position]);
      setSelectedPosId(res.data.position.id);
      setNewPos({ code: '', title: '', description: '' });
      setAddingPos(false);
    } else {
      setPosError(res.error ?? 'Failed to create position');
      toast.error(res.error ?? 'Failed to create position');
    }
  }

  async function handleToggleActive(pos: PositionDto) {
    if (pos.isActive) {
      const res = await deactivatePosition(pos.id, companyId);
      if (res.success) {
        toast.success('Position deactivated');
        setPositions((prev) =>
          prev.map((p) => (p.id === pos.id ? { ...p, isActive: false } : p)),
        );
      } else {
        toast.error(res.error ?? 'Failed to deactivate position');
      }
    } else {
      const res = await activatePosition(pos.id, companyId);
      if (res.success && res.data) {
        toast.success('Position activated');
        setPositions((prev) =>
          prev.map((p) => (p.id === pos.id ? res.data!.position : p)),
        );
      } else {
        toast.error(res.error ?? 'Failed to activate position');
      }
    }
  }

  // ── Requirement actions ─────────────────────────────────────────────────────
  function addRequirement() {
    if (!newReq.competencyId) return;
    const exists = requirements.some((r) => r.competencyId === newReq.competencyId);
    if (exists) return;
    setRequirements((prev) => [
      ...prev,
      {
        competencyId: newReq.competencyId,
        competencyCode: newReq.competencyCode,
        requiredLevel: newReq.requiredLevel,
        weight: newReq.weight,
        isCritical: newReq.isCritical,
      },
    ]);
    setNewReq({ competencyId: '', competencyCode: '', requiredLevel: 3, weight: 1, isCritical: false });
    setAddingReq(false);
    setReqDirty(true);
  }

  function removeRequirement(competencyId: string) {
    setRequirements((prev) => prev.filter((r) => r.competencyId !== competencyId));
    setReqDirty(true);
  }

  function updateRequirementField<K extends keyof PositionCompetencyRequirement>(
    competencyId: string,
    field: K,
    value: PositionCompetencyRequirement[K],
  ) {
    setRequirements((prev) =>
      prev.map((r) => (r.competencyId === competencyId ? { ...r, [field]: value } : r)),
    );
    setReqDirty(true);
  }

  async function handleSaveProfile() {
    if (!selectedPosId) return;
    setReqSaving(true);
    const res = await updatePosition(
      selectedPosId,
      { companyId, competencyRequirements: requirements },
    );
    setReqSaving(false);
    if (res.success && res.data) {
      toast.success('Competency profile saved');
      setPositions((prev) =>
        prev.map((p) => (p.id === selectedPosId ? res.data!.position : p)),
      );
      setReqDirty(false);
    } else {
      toast.error(res.error ?? 'Failed to save profile');
    }
  }

  const selectedPos = positions.find((p) => p.id === selectedPosId) ?? null;

  // competencies not yet added to this position
  const availableComps = allCompetencies.filter(
    (c) => !requirements.some((r) => r.competencyId === c.id),
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-lg bg-brand-50 shrink-0">
          <Briefcase className="w-5 h-5 text-brand-500" />
        </div>
        <div>
          <h1 className="text-xl font-semibold text-foreground tracking-tight">Positions</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Competency profiles for each role — the standard employees are developed toward
          </p>
        </div>
      </div>

      {/* Info banner */}
      <div className="flex items-start gap-2.5 rounded-lg border border-brand-100 bg-brand-50/50 px-4 py-3">
        <Info className="w-4 h-4 text-brand-500 shrink-0 mt-0.5" />
        <p className="text-xs text-brand-500 leading-relaxed">
          Each position defines required competency levels (1–5). When an employee is assigned to a
          position, their IDP development targets are driven by the gap between their current
          competency level and the position's required level.
        </p>
      </div>

      {/* Two-panel layout */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:h-[calc(100vh-300px)]">

        {/* Left: Position list */}
        <Card className="md:col-span-1 flex flex-col overflow-hidden">
          <CardHeader className="py-3 px-4 border-b shrink-0">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm">Positions</CardTitle>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 w-7 p-0"
                onClick={() => setAddingPos(true)}
              >
                <Plus className="w-3.5 h-3.5" />
              </Button>
            </div>
            <div className="flex items-center gap-1.5 mt-1">
              <button
                onClick={() => setShowInactive((v) => !v)}
                className={cn(
                  'text-xs px-2 py-0.5 rounded-full transition-colors',
                  showInactive
                    ? 'bg-muted text-foreground'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {showInactive ? 'Showing all' : 'Active only'}
              </button>
            </div>
          </CardHeader>

          <div className="flex-1 overflow-y-auto">
            {posLoading ? (
              <div className="p-3 space-y-2">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-16 bg-muted rounded-lg animate-pulse" />
                ))}
              </div>
            ) : (
              <div className="py-1">
                {/* Inline create form */}
                {addingPos && (
                   <form onSubmit={handleCreatePosition} className="px-3 py-3 space-y-2 border-b bg-accent/30">
                    <p className="text-xs font-medium">New Position</p>
                    <div>
                      <Label className="text-xs">Code</Label>
                      <Input
                        className="h-7 text-xs uppercase mt-0.5"
                        placeholder="e.g. SE-L1"
                        value={newPos.code}
                        onChange={(e) => setNewPos((f) => ({ ...f, code: e.target.value }))}
                        required
                        autoFocus
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Title</Label>
                      <Input
                        className="h-7 text-xs mt-0.5"
                        placeholder="e.g. Senior Software Engineer"
                        value={newPos.title}
                        onChange={(e) => setNewPos((f) => ({ ...f, title: e.target.value }))}
                        required
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Description (optional)</Label>
                      <Input
                        className="h-7 text-xs mt-0.5"
                        placeholder="Role summary"
                        value={newPos.description}
                        onChange={(e) => setNewPos((f) => ({ ...f, description: e.target.value }))}
                      />
                    </div>
                    {posError && <p className="text-xs text-destructive">{posError}</p>}
                    <div className="flex gap-1.5">
                      <Button type="submit" size="sm" className="h-7 text-xs flex-1" disabled={posSaving}>
                        {posSaving ? 'Saving…' : 'Create'}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="h-7 text-xs"
                        onClick={() => { setAddingPos(false); setNewPos({ code: '', title: '', description: '' }); setPosError(''); }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </form>
                )}

                {positions.length === 0 && !addingPos && (
                  <div className="p-6 text-center">
                    <Briefcase className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
                    <p className="text-xs text-muted-foreground">No positions yet.</p>
                    <button onClick={() => setAddingPos(true)} className="text-xs text-primary hover:underline mt-1">
                      Create the first position
                    </button>
                  </div>
                )}

                {positions.map((pos) => (
                  <div
                    key={pos.id}
                    onClick={() => setSelectedPosId(pos.id)}
                    className={cn(
                      'group px-3 py-2.5 cursor-pointer hover:bg-accent/50 transition-colors border-b last:border-b-0',
                      selectedPosId === pos.id && 'bg-accent',
                    )}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5 mb-0.5">
                          <Badge variant="secondary" className="text-xs font-mono shrink-0">
                            {pos.code}
                          </Badge>
                          {!pos.isActive && (
                            <Badge variant="outline" className="text-xs text-muted-foreground shrink-0">
                              Inactive
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm font-medium text-foreground truncate">{pos.title}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-muted-foreground">
                            {pos.competencyRequirements?.length ?? 0} competenc{(pos.competencyRequirements?.length ?? 0) === 1 ? 'y' : 'ies'}
                          </span>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        className={cn(
                          'h-6 shrink-0 text-xs opacity-0 group-hover:opacity-100 transition-opacity mt-0.5',
                          pos.isActive ? 'text-muted-foreground' : 'text-emerald-600',
                        )}
                        onClick={(e) => { e.stopPropagation(); handleToggleActive(pos); }}
                      >
                        {pos.isActive ? 'Deactivate' : 'Activate'}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>

        {/* Right: Competency Profile */}
        <Card className="md:col-span-2 flex flex-col overflow-hidden">
          <CardHeader className="py-3 px-4 border-b shrink-0">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-sm">
                  {selectedPos ? `${selectedPos.title} — Competency Profile` : 'Competency Profile'}
                </CardTitle>
                {selectedPos && (
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Define which competencies this role requires and at what level
                  </p>
                )}
              </div>
              {reqDirty && (
                <Button
                  size="sm"
                  onClick={handleSaveProfile}
                  disabled={reqSaving}
                  className="shrink-0"
                >
                  {reqSaving ? 'Saving…' : 'Save Profile'}
                </Button>
              )}
            </div>
          </CardHeader>

          <div className="flex-1 overflow-y-auto">
            {!selectedPos ? (
              <div className="flex flex-col items-center justify-center h-full text-center p-8">
                <Target className="w-10 h-10 text-muted-foreground/30 mb-3" />
                <p className="text-sm text-muted-foreground">Select a position to view its competency profile</p>
              </div>
            ) : (
              <div className="p-4 space-y-4">

                {/* Requirements list */}
                {requirements.length === 0 && !addingReq ? (
                  <div className="rounded-lg border border-dashed border-border p-8 text-center">
                    <Target className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">No competency requirements defined yet.</p>
                    <button
                      onClick={() => setAddingReq(true)}
                      className="text-xs text-primary hover:underline mt-1"
                    >
                      Add the first requirement
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {requirements.map((req) => {
                      const comp = allCompetencies.find((c) => c.id === req.competencyId);
                      return (
                        <Card key={req.competencyId} className="p-3">
                          <div className="flex items-start gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-2">
                                <Badge variant="secondary" className="text-xs font-mono shrink-0">
                                  {req.competencyCode}
                                </Badge>
                                {comp && (
                                  <span className="text-sm font-medium text-foreground truncate">
                                    {comp.name}
                                  </span>
                                )}
                                {req.isCritical && (
                                  <Badge className="text-xs bg-amber-100 text-amber-700 border-amber-200 shrink-0">
                                    <Star className="w-2.5 h-2.5 mr-0.5" />
                                    Critical
                                  </Badge>
                                )}
                              </div>

                              {/* Required level */}
                              <div className="space-y-1.5">
                                <div className="flex items-center justify-between">
                                  <span className="text-xs text-muted-foreground">Required Level</span>
                                  <span className="text-xs font-semibold text-brand-500">
                                    {req.requiredLevel} / 5
                                  </span>
                                </div>
                                <Slider
                                  min={1}
                                  max={5}
                                  step={1}
                                  value={[req.requiredLevel]}
                                  onValueChange={([v]) =>
                                    updateRequirementField(req.competencyId, 'requiredLevel', v)
                                  }
                                  className="w-full"
                                />
                                <div className="flex justify-between">
                                  {[1, 2, 3, 4, 5].map((l) => (
                                    <span
                                      key={l}
                                      className={cn(
                                        'text-[10px]',
                                        l <= req.requiredLevel
                                          ? 'text-brand-500 font-medium'
                                          : 'text-muted-foreground',
                                      )}
                                    >
                                      {l}
                                    </span>
                                  ))}
                                </div>
                              </div>

                              {/* Weight + Critical */}
                              <div className="flex items-center gap-4 mt-2">
                                <div className="flex items-center gap-1.5">
                                  <span className="text-xs text-muted-foreground">Weight</span>
                                  <Input
                                    type="number"
                                    min={1}
                                    max={10}
                                    className="h-6 w-14 text-xs text-center"
                                    value={req.weight}
                                    onChange={(e) =>
                                      updateRequirementField(
                                        req.competencyId,
                                        'weight',
                                        Math.max(1, Number(e.target.value)),
                                      )
                                    }
                                  />
                                </div>
                                <button
                                  className={cn(
                                    'text-xs flex items-center gap-1 px-2 py-1 rounded transition-colors',
                                    req.isCritical
                                      ? 'bg-amber-100 text-amber-700'
                                      : 'text-muted-foreground hover:bg-accent',
                                  )}
                                  onClick={() =>
                                    updateRequirementField(req.competencyId, 'isCritical', !req.isCritical)
                                  }
                                >
                                  <Star className="w-3 h-3" />
                                  {req.isCritical ? 'Critical' : 'Mark critical'}
                                </button>
                              </div>
                            </div>

                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 w-7 p-0 text-destructive hover:text-destructive shrink-0"
                              onClick={() => removeRequirement(req.competencyId)}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </Card>
                      );
                    })}
                  </div>
                )}

                {/* Add requirement row */}
                {addingReq ? (
                  <Card className="p-3 border-dashed">
                    <p className="text-xs font-medium text-foreground mb-3">Add Competency Requirement</p>
                    <div className="space-y-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Competency</Label>
                        <Select
                          value={newReq.competencyId}
                          onValueChange={(v) => {
                            const comp = allCompetencies.find((c) => c.id === v);
                            setNewReq((f) => ({
                              ...f,
                              competencyId: v,
                              competencyCode: comp?.code ?? '',
                            }));
                          }}
                        >
                          <SelectTrigger className="h-8 text-xs">
                            <SelectValue placeholder="Select competency…" />
                          </SelectTrigger>
                          <SelectContent>
                            {availableComps.length === 0 ? (
                              <div className="px-2 py-1.5 text-xs text-muted-foreground">
                                All competencies already added
                              </div>
                            ) : (
                              availableComps.map((c) => (
                                <SelectItem key={c.id} value={c.id}>
                                  <span className="font-mono text-xs mr-2">{c.code}</span>
                                  {c.name}
                                </SelectItem>
                              ))
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1.5">
                        <div className="flex items-center justify-between">
                          <Label className="text-xs">Required Level</Label>
                          <span className="text-xs font-semibold text-brand-500">{newReq.requiredLevel} / 5</span>
                        </div>
                        <Slider
                          min={1}
                          max={5}
                          step={1}
                          value={[newReq.requiredLevel]}
                          onValueChange={([v]) => setNewReq((f) => ({ ...f, requiredLevel: v }))}
                        />
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-1.5">
                          <Label className="text-xs">Weight</Label>
                          <Input
                            type="number"
                            min={1}
                            max={10}
                            className="h-6 w-14 text-xs text-center"
                            value={newReq.weight}
                            onChange={(e) =>
                              setNewReq((f) => ({ ...f, weight: Math.max(1, Number(e.target.value)) }))
                            }
                          />
                        </div>
                        <button
                          className={cn(
                            'text-xs flex items-center gap-1 px-2 py-1 rounded transition-colors',
                            newReq.isCritical
                              ? 'bg-amber-100 text-amber-700'
                              : 'text-muted-foreground hover:bg-accent',
                          )}
                          onClick={() => setNewReq((f) => ({ ...f, isCritical: !f.isCritical }))}
                        >
                          <Star className="w-3 h-3" />
                          {newReq.isCritical ? 'Critical' : 'Mark critical'}
                        </button>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          className="text-xs"
                          onClick={addRequirement}
                          disabled={!newReq.competencyId}
                        >
                          <Check className="w-3.5 h-3.5 mr-1" />
                          Add
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-xs"
                          onClick={() => {
                            setAddingReq(false);
                            setNewReq({ competencyId: '', competencyCode: '', requiredLevel: 3, weight: 1, isCritical: false });
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  </Card>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full text-xs gap-1.5"
                    onClick={() => setAddingReq(true)}
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Add Competency Requirement
                  </Button>
                )}

                <Separator />

                {/* Summary */}
                {requirements.length > 0 && (
                  <div className="rounded-lg bg-accent/50 p-3">
                    <p className="text-xs font-medium text-foreground mb-2">Profile Summary</p>
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div>
                        <p className="text-lg font-semibold text-foreground">{requirements.length}</p>
                        <p className="text-xs text-muted-foreground">Competencies</p>
                      </div>
                      <div>
                        <p className="text-lg font-semibold text-amber-600">
                          {requirements.filter((r) => r.isCritical).length}
                        </p>
                        <p className="text-xs text-muted-foreground">Critical</p>
                      </div>
                      <div>
                        <p className="text-lg font-semibold text-brand-500">
                          {requirements.length > 0
                            ? (requirements.reduce((s, r) => s + r.requiredLevel, 0) / requirements.length).toFixed(1)
                            : '—'}
                        </p>
                        <p className="text-xs text-muted-foreground">Avg Level</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
